import React, { useState } from 'react';
import { Lightbulb, Check, X, Loader2 } from 'lucide-react';
import { useStrategyLabStore } from '../../stores/strategyLabStore';
import type { Recommendation, RecommendationSeverity } from '../../types/strategyLab';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

interface RecommendationCardProps {
  deploymentId: string;
  rec: Recommendation;
}

const SEVERITY_TONE: Record<RecommendationSeverity, string> = {
  info: 'bg-sky-500/10 text-sky-500 border-sky-500/30',
  low: 'bg-slate-500/10 text-slate-400 border-slate-500/30',
  medium: 'bg-amber-500/10 text-amber-500 border-amber-500/30',
  high: 'bg-orange-500/10 text-orange-500 border-orange-500/30',
  critical: 'bg-rose-500/10 text-rose-500 border-rose-500/30',
};

const RecommendationCard: React.FC<RecommendationCardProps> = ({ deploymentId, rec }) => {
  const acceptRecommendation = useStrategyLabStore((s) => s.acceptRecommendation);
  const dismissRecommendation = useStrategyLabStore((s) => s.dismissRecommendation);
  const [busy, setBusy] = useState<'accept' | 'dismiss' | null>(null);

  const onAccept = async () => {
    setBusy('accept');
    await acceptRecommendation(deploymentId, rec.id);
    setBusy(null);
  };
  const onDismiss = async () => {
    setBusy('dismiss');
    await dismissRecommendation(deploymentId, rec.id);
    setBusy(null);
  };

  const tone = SEVERITY_TONE[rec.severity] ?? SEVERITY_TONE.info;

  return (
    <div className={`rounded-md border p-3 space-y-2 ${tone}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Lightbulb className="w-4 h-4 shrink-0" />
          <span className="text-sm font-medium truncate">{rec.type.replace(/_/g, ' ')}</span>
        </div>
        <Badge variant="outline" className="shrink-0 text-[10px] uppercase border-current">
          {rec.severity}
        </Badge>
      </div>

      <p className="text-xs text-foreground/80 leading-relaxed">{rec.rationale}</p>

      {typeof rec.confidence === 'number' && (
        <p className="text-[11px] text-muted-foreground">
          Confidence {(rec.confidence * 100).toFixed(0)}%
        </p>
      )}

      <div className="flex gap-2 pt-1">
        <Button size="sm" variant="outline" className="border-current" disabled={busy !== null} onClick={onAccept}>
          {busy === 'accept' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          Apply
        </Button>
        <Button size="sm" variant="ghost" disabled={busy !== null} onClick={onDismiss}>
          {busy === 'dismiss' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
          Dismiss
        </Button>
      </div>
    </div>
  );
};

export default RecommendationCard;
